import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '@/config/api';

const API = getApiUrl();

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  
  // Impersonation state
  const [originalSuperAdmin, setOriginalSuperAdmin] = useState(null);
  const [originalToken, setOriginalToken] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    // Check for stored impersonation state on mount FIRST
    const storedImpersonation = localStorage.getItem('impersonation_state');
    if (storedImpersonation) {
      try {
        const { originalUser, originalAuthToken, impersonatedUser, impersonatedToken } = JSON.parse(storedImpersonation);
        setOriginalSuperAdmin(originalUser);
        setOriginalToken(originalAuthToken);
        setUser(impersonatedUser);
        setToken(impersonatedToken);
        setIsImpersonating(true);
        setLoading(false);
        return;
      } catch (e) {
        localStorage.removeItem('impersonation_state');
      }
    }
    
    if (token) {
      validateToken();
    } else {
      setLoading(false);
    }
  }, []);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUser(response.data.user);
      } else {
        logout();
      }
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data.success) {
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('auth_token', newToken);
      setToken(newToken);
      setUser(userData);
      return { success: true, user: userData };
    }
    return { success: false, error: 'Login failed' };
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
  };

  const updateUser = async (updates) => {
    try {
      const response = await axios.put(`${API}/auth/me`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUser(response.data.user);
        return { success: true };
      }
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Update failed' };
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      const response = await axios.post(`${API}/auth/reset-password-request`, { email });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Request failed' };
    }
  };

  const resetPassword = async (resetToken, newPassword) => {
    try {
      const response = await axios.post(`${API}/auth/reset-password`, { 
        token: resetToken, 
        new_password: newPassword 
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Reset failed' };
    }
  };

  // Set auth from external token (used for account setup)
  const setAuthFromToken = (newToken, userData) => {
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  // Helper to get auth headers
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  // Impersonate a user (Super Admin only)
  const impersonateUser = async (targetUserId) => {
    if (!user || user.role !== 'super_admin') {
      return { success: false, error: 'Only super admins can impersonate users' };
    }
    
    try {
      // Get the target user data and a token for them
      const response = await axios.post(`${API}/admin/impersonate/${targetUserId}`, {}, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        const { user: impersonatedUserData, token: impersonatedToken } = response.data;
        
        // Store original state for switching back
        const impersonationState = {
          originalUser: user,
          originalAuthToken: token,
          impersonatedUser: impersonatedUserData,
          impersonatedToken: impersonatedToken
        };
        localStorage.setItem('impersonation_state', JSON.stringify(impersonationState));
        
        // Update state to impersonated user
        setOriginalSuperAdmin(user);
        setOriginalToken(token);
        setUser(impersonatedUserData);
        setToken(impersonatedToken);
        setIsImpersonating(true);
        
        return { success: true };
      }
      return { success: false, error: 'Failed to impersonate user' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to impersonate user' };
    }
  };

  // Stop impersonating and return to super admin
  const stopImpersonating = () => {
    if (!isImpersonating || !originalSuperAdmin || !originalToken) {
      return;
    }
    
    // Restore original super admin state
    setUser(originalSuperAdmin);
    setToken(originalToken);
    localStorage.setItem('auth_token', originalToken);
    
    // Clear impersonation state
    localStorage.removeItem('impersonation_state');
    setOriginalSuperAdmin(null);
    setOriginalToken(null);
    setIsImpersonating(false);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    requestPasswordReset,
    resetPassword,
    setAuthFromToken,
    getAuthHeaders,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin' || (isImpersonating && originalSuperAdmin?.role === 'super_admin'),
    isDirector: user?.role === 'director' || user?.role === 'super_admin',
    isActor: user?.role === 'actor',
    // Impersonation
    isImpersonating,
    originalSuperAdmin,
    impersonateUser,
    stopImpersonating
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
